"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FilterBase = void 0;
const components_1 = require("@apitable/components");
const core_1 = require("@apitable/core");
const icons_1 = require("@apitable/icons");
const immer_1 = __importDefault(require("immer"));
const lodash_1 = require("lodash");
const react_1 = __importStar(require("react"));
const react_redux_1 = require("react-redux");
const filter_value_1 = require("./filter_value");
const field_select_1 = require("./field_select");
const helper_1 = require("./helper");
const styled_1 = require("./styled");
const filter_button_1 = require("./filter_button");
const store_1 = require("../../store");
const transformNullFilter = (filter) => {
    return filter == null || (0, lodash_1.isEqual)(filter, core_1.EmptyNullOperand) ? {
        operator: core_1.OperatorEnums.And,
        operands: [],
    } : filter;
};
const WarningTip = (props) => {
    const theme = (0, components_1.useTheme)();
    return react_1.default.createElement(components_1.Box, { display: 'flex', alignItems: 'center', gridColumn: 'property-start / value-end' },
        react_1.default.createElement(icons_1.WarnCircleFilled, { color: theme.color.fc10 }),
        react_1.default.createElement(components_1.Typography, { color: theme.color.fc10, variant: "body3", style: { marginLeft: '4px' } }, props.children));
};
/**
 * This is a recursively rendered component with up to 3 levels of nesting. Renders S-expressions as nested grouped conditional filters.
 * Supports adding, removing and modifying filter conditions.
 * Expression Classification
 * + Basic expressions
 *   + The first operand is always a field of the specified table
 *   + The operator is the specified operator. bool Related
 *   + The third is the user input value, which can be static or dynamic
 * + Grouping expressions
 *   + or / and concatenated expressions. It can be a base expression or a grouping expression
 */
const FilterBase = (props) => {
    var _a, _b, _c, _d, _e;
    const supportsGrouping = false;
    const { datasheetId, hasParent = false, onChange, depth = 0 } = props;
    // Null expressions converted to null
    const [filter, setFilter] = (0, react_1.useState)(transformNullFilter(props.filter));
    const theme = (0, components_1.useTheme)();
    const isRoot = !hasParent;
    const updateFilter = (0, react_1.useCallback)((filter) => {
        var _a;
        setFilter(filter);
        // The updated value of the child component is passed to the parent component. 
        // The parent component knows the specific path of the child component and only needs to pass the value.
        if (onChange) {
            // root update, you need to serialize the values a bit.
            if (isRoot) {
                onChange({
                    type: core_1.OperandTypeEnums.Expression,
                    value: ((_a = filter.operands) === null || _a === void 0 ? void 0 : _a.length) === 0 ? null : filter, // Null handling
                });
            }
            else {
                onChange(filter);
            }
        }
    }, [onChange, isRoot]);
    (0, react_1.useEffect)(() => {
        setFilter(transformNullFilter(props.filter));
    }, [props.filter]);
    const isGroup = (0, lodash_1.isEqual)(filter, core_1.EmptyNullOperand) || filter.operator === 'and' || filter.operator === 'or';
    const isBaseExpression = !isGroup;
    /**
     * Modify its own value inside the component, and at the same time to synchronize it to the parent component.
     * @param path: The path of the subcomponent
     * @param value: The value of the subcomponent
     */
    const handleChange = (path, value) => {
        var _a;
        // Here immer and lodash set do not match, direct json to
        const _filter = JSON.parse(JSON.stringify(filter));
        // Reset the operator and reset the value when the selected field changes.
        if (path === 'operands[0].value') {
            const field = fields.find(field => field.id === value);
            const op = field ? (_a = core_1.Field.bindModel(field).acceptFilterOperators) === null || _a === void 0 ? void 0 : _a[0] : '';
            op && (0, lodash_1.set)(_filter, 'operator', op);
            // If it is a checkbox, fill in false by default.
            field && (0, lodash_1.set)(_filter, 'operands[1].value', field.type === core_1.FieldType.Checkbox ? false : null);
        }
        (0, lodash_1.set)(_filter, path, value);
        updateFilter(_filter);
    };
    // Press path to delete.
    const deleteOperandByIndex = (operandIndex) => {
        const _filter = (0, immer_1.default)(filter, (draft) => {
            draft.operands.splice(operandIndex, 1);
        });
        updateFilter(_filter);
    };
    const snapshot = (0, react_redux_1.useSelector)(state => {
        return (0, store_1.getSnapshot)(state, datasheetId);
    });
    const fieldPermissionMap = (0, react_redux_1.useSelector)(state => {
        return (0, store_1.getFieldPermissionMap)(state, datasheetId);
    });
    // Here are all the fields, with or without permissions.
    const fieldMap = snapshot.meta.fieldMap;
    const fields = (0, helper_1.getFields)((_a = snapshot.meta.views[0]) === null || _a === void 0 ? void 0 : _a.columns, fieldMap);
    const primaryField = fields[0];
    const addNewFilter = (0, react_1.useCallback)((type) => {
        const newFilter = (0, helper_1.addNewFilter)(filter, type, primaryField);
        updateFilter(newFilter);
    }, [filter, updateFilter, primaryField]);
    const boolOperatorOptions = [
        { value: 'and', label: (0, helper_1.getBooleanOptionName)('and') },
        { value: 'or', label: (0, helper_1.getBooleanOptionName)('or') },
    ];
    if (isBaseExpression) {
        const fieldId = (_b = filter.operands[0]) === null || _b === void 0 ? void 0 : _b.value;
        const field = fieldMap[fieldId];
        const isDeletedField = field == null;
        if (isDeletedField) {
            return react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(WarningTip, null, (0, core_1.t)(core_1.Strings.robot_trigger_record_matches_condition_invalid_field)));
        }
        const isCryptoField = (0, store_1.getFieldRoleByFieldId)(fieldPermissionMap, fieldId) === core_1.ConfigConstant.Role.None;
        if (isCryptoField) {
            return react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(WarningTip, null, (0, core_1.t)(core_1.Strings.robot_trigger_record_matches_condition_cannot_access_field)));
        }
        const operatorOptions = (0, helper_1.getOperatorOptions)(field);
        return (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(field_select_1.FieldSelect, { fields: fields, value: (_c = filter.operands[0]) === null || _c === void 0 ? void 0 : _c.value, onChange: (value) => handleChange('operands[0].value', value) }),
            react_1.default.createElement(components_1.Select, { options: operatorOptions, value: filter.operator, onSelected: ({ value }) => handleChange('operator', value) }),
            react_1.default.createElement(filter_value_1.FilterValue, { field: field, operator: filter.operator, value: (_d = filter.operands[1]) === null || _d === void 0 ? void 0 : _d.value, onChange: (value) => handleChange('operands[1].value', value) })));
    }
    const Wrapper = hasParent ? styled_1.SubGroupWrap : react_1.default.Fragment;
    return (react_1.default.createElement(Wrapper, null,
        react_1.default.createElement(styled_1.GroupWrapperWithButton, null,
            react_1.default.createElement(styled_1.FilterGroupWrap, null, (_e = filter === null || filter === void 0 ? void 0 : filter.operands) === null || _e === void 0 ? void 0 : _e.map((item, index) => {
                const path = `operands[${index}].value`;
                return react_1.default.createElement(react_1.default.Fragment, { key: path },
                    index == 0 ? react_1.default.createElement(styled_1.OperatorWrap, null, (0, core_1.t)(core_1.Strings.where)) : (index === 1 ? react_1.default.createElement(components_1.Select, { options: boolOperatorOptions, value: filter.operator, onSelected: ({ value }) => handleChange('operator', value) }) :
                        react_1.default.createElement(styled_1.OperatorWrap, null,
                            " ",
                            (0, helper_1.getBooleanOptionName)(filter.operator))),
                    react_1.default.createElement(exports.FilterBase, { path: path, filter: item.value, datasheetId: datasheetId, hasParent: true, depth: depth + 1, onChange: (value) => {
                            handleChange(path, value);
                        } }),
                    react_1.default.createElement(components_1.IconButton, { icon: () => react_1.default.createElement(icons_1.DeleteOutlined, { size: 16, color: theme.color.textCommonTertiary }), onClick: () => deleteOperandByIndex(index) }));
            })),
            react_1.default.createElement(styled_1.FilterButtonWrap, null,
                react_1.default.createElement(filter_button_1.FilterButton, { onClick: () => addNewFilter(helper_1.FilterTypeEnums.Filter) }, (0, core_1.t)(core_1.Strings.add_filter)),
                supportsGrouping && depth < 2 && react_1.default.createElement(components_1.Button, { prefixIcon: react_1.default.createElement(icons_1.AddOutlined, null), variant: "fill", onClick: () => {
                        addNewFilter(helper_1.FilterTypeEnums.FilterGroup);
                    } }, "Add filter criteria group")))));
};
exports.FilterBase = FilterBase;
